import React, { useEffect, useState } from "react";
import { collection, onSnapshot } from "firebase/firestore";
import { db } from "../../firebase";

export default function ParametersTable({ brand, model }) {
  const [rows, setRows] = useState([]);
  const [showGenerator, setShowGenerator] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generationStep, setGenerationStep] = useState("");
  const [generatedCode, setGeneratedCode] = useState("");
  const [showCopied, setShowCopied] = useState(false);
  const [calculatedValues, setCalculatedValues] = useState({});
  const [generatorTitle, setGeneratorTitle] = useState("PID Block Generator");

  useEffect(() => {
    if (!brand || !model) return;

    // Path: EIS/{brand}/Models/{model}/ParameterIDs
    const ref = collection(db, "EIS", brand, "Models", model, "ParameterIDs");

    const unsubscribe = onSnapshot(ref, (snap) => {
      setRows(snap.docs.map((d) => ({ id: d.id, ...d.data() })));
    });

    return () => unsubscribe();
  }, [brand, model]);

  // Fetch Calculated Values
  useEffect(() => {
    if (!brand || !model) return;
    const ref = collection(db, "EIS", brand, "Models", model, "CalculatedValues");
    const unsubscribe = onSnapshot(ref, (snap) => {
      const data = {};
      snap.docs.forEach(d => {
        data[d.id] = d.data();
      });
      setCalculatedValues(data);
    });
    return () => unsubscribe();
  }, [brand, model]);

  const generatePidBlock = () => {
    setGeneratorTitle("PID Block Generator");
    setShowGenerator(true);
    setGenerating(true);
    setGeneratedCode("");

    // Simulation steps
    const steps = [
      "Analyzing PIDs...",
      "Grouping by ranges...",
      "Optimizing for Arduino...",
      "Almost there..."
    ];

    let stepIdx = 0;
    setGenerationStep(steps[0]);

    const interval = setInterval(() => {
      stepIdx++;
      if (stepIdx < steps.length) {
        setGenerationStep(steps[stepIdx]);
      }
    }, 800);

    setTimeout(() => {
      clearInterval(interval);

      // Actual Logic
      const pids = rows.map(r => parseInt(r.pidHex || r.pid, 16)).filter(n => !isNaN(n)).sort((a, b) => a - b);

      let code = `// Auto-generated PID Block for ${brand} ${model}\n`;
      code += `// Total PIDs: ${pids.length}\n\n`;
      code += `uint8_t supportedPIDs[] = {\n`;

      // Group by 0x20 ranges
      const ranges = [
        { start: 0x00, end: 0x1F, label: "0x00–0x1F" },
        { start: 0x20, end: 0x3F, label: "0x20–0x3F" },
        { start: 0x40, end: 0x5F, label: "0x40–0x5F" },
        { start: 0x60, end: 0x7F, label: "0x60–0x7F" },
        { start: 0x80, end: 0x9F, label: "0x80–0x9F" },
        { start: 0xA0, end: 0xBF, label: "0xA0–0xBF" },
        { start: 0xC0, end: 0xDF, label: "0xC0–0xDF" },
        { start: 0xE0, end: 0xFF, label: "0xE0–0xFF" },
      ];

      let hasContent = false;

      ranges.forEach((range, idx) => {
        const rangePids = pids.filter(p => p >= range.start && p <= range.end);

        if (rangePids.length > 0) {
          if (hasContent) code += `\n\n`;
          code += `  /* ${range.label} */\n  `;

          rangePids.forEach((p, i) => {
            code += `0x${p.toString(16).toUpperCase().padStart(2, '0')}`;
            if (i < rangePids.length - 1) code += `, `;
          });

          // Add comma if not the absolute last group (simplified checks)
          const remainingPids = pids.filter(p => p > range.end);
          if (remainingPids.length > 0) code += `,`;

          hasContent = true;
        }
      });

      code += `\n};`;

      setGeneratedCode(code);
      setGenerating(false);

    }, 3500);
  };

  const generatePidSwitchBlock = () => {
    setGeneratorTitle("PID Switch Generator");
    setShowGenerator(true);
    setGenerating(true);
    setGeneratedCode("");

    const steps = [
      "Analyzing PID formulas...",
      "Mapping bytes (A/B/C/D)...",
      "Generating Arduino switch...",
      "Finalizing block..."
    ];

    let stepIdx = 0;
    setGenerationStep(steps[0]);

    const interval = setInterval(() => {
      stepIdx++;
      if (stepIdx < steps.length) {
        setGenerationStep(steps[stepIdx]);
      }
    }, 700);

    setTimeout(() => {
      clearInterval(interval);

      let code = `// Auto-generated PID decode switch for ${brand} ${model}\n\n`;
      code += `switch (pid) {\n\n`;

      rows
        .filter(r => r.pidHex && r.formula)
        .sort((a, b) => parseInt(a.pidHex, 16) - parseInt(b.pidHex, 16))
        .forEach(r => {

          const pid = r.pidHex.toUpperCase();
          const name = r.name || "PID";
          const unit = r.unit || "";
          const formula = typeof r.formula === "object" ? (r.formula.arduino || r.formula.expr) : r.formula;

          // Skip non-decodable
          if (
            !formula ||
            formula === "raw" ||
            formula === "bitmap" ||
            formula === "enum" ||
            formula === "special"
          ) {
            code += `  case ${pid}:\n`;
            code += `    Serial.println("${name} (no decode)");\n`;
            code += `    break;\n\n`;
            return;
          }

          const isInt =
            formula.includes("A-40") ||
            formula.includes("256*A+B");

          code += `  case ${pid}: {\n`;
          code += `    ${isInt ? "int" : "float"} v = ${formula};\n`;
          code += `    Serial.printf("${name}: ${isInt ? "%d" : "%.2f"} ${unit}\\n", v);\n`;
          code += `    sendPIDValueToCloud(pid, String(v));\n`;
          code += `    break;\n`;
          code += `  }\n\n`;
        });

      code += `  default:\n`;
      code += `    Serial.println("PID supported (no decode)");\n`;
      code += `    break;\n`;
      code += `}\n`;

      setGeneratedCode(code);
      setGenerating(false);

    }, 3000);
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(generatedCode);
    setShowCopied(true);
    setTimeout(() => setShowCopied(false), 2000);
  };

  if (rows.length === 0) {
    return (
      <div className="text-slate-400 text-sm">
        No parameters mapped yet.
      </div>
    );
  }

  return (
    <>
      {/* Generator Modal */}
      {showGenerator && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in">
          <div className="bg-white rounded-2xl shadow-2xl w-full max-w-lg overflow-hidden flex flex-col">
            <div className="p-5 border-b border-slate-100 flex justify-between items-center bg-slate-50">
              <h3 className="text-sm font-black text-slate-800 uppercase flex items-center gap-2">
                <svg className="w-4 h-4 text-indigo-500" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 10V3L4 14h7v7l9-11h-7z" /></svg>
                {generatorTitle}
              </h3>
              <button onClick={() => setShowGenerator(false)} className="text-slate-400 hover:text-slate-600">
                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" /></svg>
              </button>
            </div>

            <div className="p-6">
              {generating ? (
                <div className="flex flex-col items-center justify-center py-8 space-y-4">
                  <div className="relative">
                    <div className="w-12 h-12 rounded-full border-4 border-indigo-100 border-t-indigo-600 animate-spin"></div>
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-[8px]">AI</span>
                    </div>
                  </div>
                  <p className="text-sm font-bold text-slate-500 animate-pulse">{generationStep}</p>
                </div>
              ) : (
                <div className="space-y-4">
                  <div className="bg-slate-900 rounded-lg p-4 custom-scrollbar overflow-auto max-h-[50vh] relative group">
                    <pre className="text-[10px] font-mono text-cyan-300 leading-relaxed">
                      {generatedCode}
                    </pre>
                    <button
                      onClick={copyToClipboard}
                      className="absolute top-2 right-2 bg-white/10 hover:bg-white/20 text-white p-1.5 rounded transition-colors opacity-0 group-hover:opacity-100"
                      title="Copy to Clipboard"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" /></svg>
                    </button>
                  </div>
                  <div className="flex justify-end items-center gap-3">
                    {showCopied && (
                      <div className="text-[10px] font-bold text-green-600 bg-green-50 px-2 py-1 rounded-md animate-fade-in flex items-center gap-1.5">
                        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7" /></svg>
                        Copied!
                      </div>
                    )}
                    <button
                      onClick={copyToClipboard}
                      className="bg-indigo-600 text-white px-4 py-2 rounded-lg text-xs font-bold shadow-lg shadow-indigo-200 hover:bg-indigo-700 transition-all flex items-center gap-2"
                    >
                      <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5H6a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2v-1M8 5a2 2 0 002 2h2a2 2 0 002-2M8 5a2 2 0 012-2h2a2 2 0 012 2m0 0h2a2 2 0 012 2v3m2 4H10m0 0l3-3m-3 3l3 3" /></svg>
                      Copy Code
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      <div className="space-y-2">
        <div className="flex items-center justify-between px-1">
          <span className="text-xs font-bold text-slate-500 uppercase tracking-widest">Mapped List</span>

          <div className="flex items-center gap-2">
            <button
              onClick={generatePidBlock}
              className="bg-indigo-50 text-indigo-600 p-1.5 rounded-lg hover:bg-indigo-100 transition-colors flex items-center gap-1.5"
              title="Generate PID Block"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.384-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" /></svg>
              <span className="text-[10px] font-bold">Block</span>
            </button>
            <button
              onClick={generatePidSwitchBlock}
              className="bg-indigo-50 text-indigo-600 p-1.5 rounded-lg hover:bg-indigo-100 transition-colors flex items-center gap-1.5"
              title="Generate Switch Block"
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 20l4-16m4 4l4 4-4 4M6 16l-4-4 4-4" /></svg>
              <span className="text-[10px] font-bold">Switch AI</span>
            </button>
            <span className="bg-slate-200 text-slate-600 text-[10px] font-bold px-2 py-0.5 rounded-full">
              {rows.length}
            </span>
          </div>
        </div>
        <table className="w-full bg-white rounded-xl shadow text-xs">
          <thead className="bg-slate-100 text-slate-500 uppercase">
            <tr>
              <th className="p-3 text-left">PID</th>
              <th className="p-3 text-left">Dec</th>
              <th className="p-3 text-left">Source</th>
              <th className="p-3 text-left">Bytes</th>
              <th className="p-3 text-left">Mapped Parameter</th>
              <th className="p-3 text-left">Formula</th>
              <th className="p-3 text-left">Unit</th>
              <th className="p-3 text-left">Value</th>
              <th className="p-3 text-left">Updated</th>
              <th className="p-3 text-left">Discovered</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => (
              <tr key={r.id} className="border-t hover:bg-slate-50">
                {/* PID Column (Hex) */}
                <td className="p-3 font-mono font-bold text-slate-700">
                  {r.pidHex || r.pid}
                  {r.overridden && (
                    <span className="ml-2 px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 text-[10px] font-bold">
                      EDITED
                    </span>
                  )}
                </td>

                {/* Decimal PID */}
                <td className="p-3 font-mono text-slate-500">
                  {r.pidDec || "-"}
                </td>

                {/* Source */}
                <td className="p-3">
                  <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${r.source === "OBD"
                    ? "bg-purple-100 text-purple-700"
                    : "bg-slate-100 text-slate-600"
                    }`}>
                    {r.source || "UNKNOWN"}
                  </span>
                </td>

                {/* Bytes */}
                <td className="p-3 font-mono text-slate-500">
                  {r.bytes !== null && r.bytes !== undefined ? r.bytes : "-"}
                </td>

                {/* Mapped Parameter */}
                <td className="p-3">
                  {r.mappedParameter ? (
                    <span className="bg-blue-100 text-blue-700 px-2 py-1 rounded font-bold">
                      {r.mappedParameter}
                    </span>
                  ) : (
                    <span className="text-slate-300 italic">Unmapped</span>
                  )}
                </td>

                {/* Formula */}
                <td className="p-3 font-mono text-slate-500">
                  {r.formula && typeof r.formula === "object"
                    ? (r.formula.expr || r.formula.arduino || JSON.stringify(r.formula))
                    : (r.formula || "-")}
                </td>

                {/* Unit */}
                <td className="p-3 text-slate-500 font-medium">
                  {r.unit || "-"}
                </td>

                {/* Calculated Value */}
                <td className="p-3 font-mono font-bold text-indigo-600">
                  {(() => {
                    // Try to find a match for this row in calculatedValues map
                    // ID might be r.id, r.pidHex (without 0x?), r.pid
                    // The Cloud Function uses pid.toUpperCase()

                    const keysToCheck = [
                      r.id,
                      r.pidHex,
                      r.pid,
                      (r.pidHex || "").toUpperCase(), // 0X0C
                      (r.pidHex || "").toLowerCase(), // 0x0c
                      (r.pidHex || "").replace("0x", ""), // 0c
                      (r.pidHex || "").replace("0x", "").toUpperCase(), // 0C
                      `0X${(r.pidHex || "").replace("0x", "").toUpperCase()}` // Force 0X prefix
                    ];

                    let match = null;
                    for (const k of keysToCheck) {
                      if (k && calculatedValues[k]) {
                        match = calculatedValues[k];
                        break;
                      }
                    }

                    return match !== null && match.calculatedValue !== undefined
                      ? match.calculatedValue
                      : "-";
                  })()}
                </td>

                {/* Last Updated */}
                <td className="p-3 text-[10px] text-slate-400 font-mono">
                  {(() => {
                    const keysToCheck = [
                      r.id,
                      r.pidHex,
                      r.pid,
                      (r.pidHex || "").toUpperCase(), // 0X0C
                      (r.pidHex || "").toLowerCase(), // 0x0c
                      (r.pidHex || "").replace("0x", ""), // 0c
                      (r.pidHex || "").replace("0x", "").toUpperCase(), // 0C
                      `0X${(r.pidHex || "").replace("0x", "").toUpperCase()}` // Force 0X prefix
                    ];

                    let match = null;
                    for (const k of keysToCheck) {
                      if (k && calculatedValues[k]) {
                        match = calculatedValues[k];
                        break;
                      }
                    }

                    return match?.lastUpdatedAt?.toDate
                      ? match.lastUpdatedAt.toDate().toLocaleTimeString()
                      : "-";
                  })()}
                </td>

                {/* Discovered At */}
                <td className="p-3 text-slate-400">
                  {r.discoveredAt?.toDate?.().toLocaleString() || "-"}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}
